import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import type { CreateCampaignApiArg } from '@/__generated/es-api'
import { clientRef } from '@/app/client-ref'
import { ERROR_SAVE_CAMPAIGN_SERVER } from '@/app/module/campaigns/definitions'
import type { CampaignSaveData } from '@/app/module/campaigns/types/save-data'
import { toCampaignCreateBody } from '@/app/module/campaigns/utils/campaign-payload'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

// Used by the chatbot editor.
export const useCreateCampaign = () => {
  const orgId = useSelector(selectOrgId)
  const dispatch = useAppDispatch()
  const [create, result] = esApi.endpoints.createCampaign.useMutation()

  const createCampaign = useCallback(
    async (body: CampaignSaveData<CreateCampaignApiArg['body']>) => {
      try {
        const res = await create({
          orgId: orgId.toString(),
          body: { ...toCampaignCreateBody(body), clientRef },
        }).unwrap()
        dispatch(
          createNotification({
            'create-campaign-success': {
              type: 'success',
              message: 'Campaign saved successfully.',
            },
          }),
        )
        return res
      } catch (error) {
        dispatch(
          createNotification({
            'create-campaign-failure': {
              type: 'error',
              message: getErrorMessage(error, ERROR_SAVE_CAMPAIGN_SERVER),
            },
          }),
        )
        return undefined
      }
    },
    [create, dispatch, orgId],
  )

  return [createCampaign, result] as const
}
